import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Department } from "../../lib/kiosk-client";
import { kioskClient } from "../../lib/kiosk-client";
import type { FlowState } from "../../pages/FlowPage";
import { BigButton } from "../BigButton";

interface Props {
  state: FlowState;
  onNext: (update: Partial<FlowState>) => void;
}

export function DeptStep({ state, onNext }: Props) {
  const { t } = useTranslation();
  const [depts, setDepts] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    kioskClient
      .getDepartments()
      .then(({ data }) => setDepts(data))
      .catch(() => setDepts([]))
      .finally(() => setLoading(false));
  }, []);

  // Auto-skip when fetch is done and there are no departments.
  useEffect(() => {
    if (!loading && depts.length === 0) {
      onNext({ department_id: undefined });
    }
  }, [loading, depts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-[var(--kiosk-brand-muted)] text-2xl">
          Loading...
        </span>
      </div>
    );
  }

  if (depts.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 p-8 h-full justify-center">
      <h2 className="text-3xl font-bold text-center">{t("flow.dept.title")}</h2>
      <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto">
        {depts.map((dept) => (
          <BigButton
            key={dept.id}
            variant={state.department_id === dept.id ? "primary" : "secondary"}
            fullWidth
            onClick={() => onNext({ department_id: dept.id })}
          >
            {dept.name}
          </BigButton>
        ))}
        <BigButton
          variant="secondary"
          fullWidth
          onClick={() => onNext({ department_id: undefined })}
        >
          {t("flow.dept.skip")}
        </BigButton>
      </div>
    </div>
  );
}
